/**
 * Platform route factory. The shell spreads these under the authed route:
 *
 *   children: [...platformRoutes(authedRoute)]
 *
 * Pages stay router-free; this file is the only place platform code touches
 * the router and the only place it reads the session. Deciding, delegating,
 * marking read and writing a branch are each gated on the exact permission
 * the generated route policy demands.
 */
import { createRoute, type AnyRoute } from "@tanstack/react-router";

import { useSession } from "@/lib/session/use-session";

import { ApprovalsPage } from "./pages/approvals-page";
import { NotificationsPage } from "./pages/notifications-page";
import { BranchesPage } from "./pages/branches-page";
import { AccountPage } from "./pages/account-page";
import { AuditLogPage } from "./pages/audit-log-page";
import { PLATFORM_PERMISSIONS as P, PlatformPermissionBoundary } from "./permissions";

function ApprovalsRoute() {
  const { hasPermission, user } = useSession();
  return (
    <PlatformPermissionBoundary permission={P.approvalRead}>
      <ApprovalsPage
        {...(user === null ? {} : { currentUserId: user.id })}
        canDecide={hasPermission(P.approvalDecide)}
        canDelegate={hasPermission(P.approvalDelegate)}
        canReadUsers={hasPermission(P.userRead)}
      />
    </PlatformPermissionBoundary>
  );
}

function NotificationsRoute() {
  const { hasPermission } = useSession();
  return (
    <PlatformPermissionBoundary permission={P.notificationRead}>
      <NotificationsPage canManage={hasPermission(P.notificationManage)} />
    </PlatformPermissionBoundary>
  );
}

function BranchesRoute() {
  const { hasPermission } = useSession();
  return (
    <PlatformPermissionBoundary permission={P.branchRead}>
      <BranchesPage canWrite={hasPermission(P.branchWrite)} />
    </PlatformPermissionBoundary>
  );
}

function AuditLogRoute() {
  return (
    <PlatformPermissionBoundary permission={P.auditRead}>
      <AuditLogPage />
    </PlatformPermissionBoundary>
  );
}

export function platformRoutes(parent: AnyRoute): AnyRoute[] {
  return [
    // No permission boundary: this page acts on the caller's own account, and
    // every operation behind it is session-authenticated rather than granted.
    createRoute({
      getParentRoute: () => parent,
      path: "account",
      component: AccountPage,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "admin/approvals",
      component: ApprovalsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "admin/notifications",
      component: NotificationsRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "admin/branches",
      component: BranchesRoute,
    }),
    createRoute({
      getParentRoute: () => parent,
      path: "admin/audit-log",
      component: AuditLogRoute,
    }),
  ] as AnyRoute[];
}
